/**
 * Serves the `pane_tool_call` events of one streamed turn (contract §3.1). gyro-svc asks the host to run a tool: an
 * Office.js read or write, or a Microsoft Graph read with the person's own token. The host's `clientTools.execute`
 * runs it, and the outcome goes to `clientTools.resultPath` with the call's one-time capability. The rules match the
 * desktop's (desktop/src/Gyro.ClientActions/DeviceToolRunner.cs), so both ends of the channel behave alike:
 *  - The stream reader never waits: each call runs on its own and the reader goes straight on.
 *  - `deadlineMs` is how long the server waits (clamped to 5-60 s, default 30 s). The host gets that less a 5 s reserve
 *    for posting the answer, but never less than half of it. A call still running then is answered `m365_unavailable`,
 *    so the server does not time it out: a timeout trips its breaker and refuses every later call of the turn.
 *  - A call that throws, answers malformed, or returns more than 32 KB of JSON is answered as an error. A code outside
 *    the closed-set shape becomes `m365_unavailable`; the server maps codes to model-facing sentences.
 *  - Each call id runs once, at most 24 per turn. Calls of a stopped turn are abandoned, never posted.
 *  - Nothing about the input or the result is logged: only the tool, the outcome code and the HTTP status.
 */
import { isServiceApiPath } from './paneHost.js';
export const PANE_TOOL_MAX_CALLS_PER_TURN = 24;
export const PANE_TOOL_RESULT_MAX_BYTES = 32 * 1024;
export const PANE_TOOL_DEFAULT_DEADLINE_MS = 30_000;
const MIN_DEADLINE_MS = 5_000;
const MAX_DEADLINE_MS = 60_000;
/** The share of the server's wait kept for posting the answer. */
const POST_RESERVE_MS = 5_000;
const MAX_MESSAGE_CHARS = 200;
/** The closed set's catch-all failure (contract §4.5). */
export const PANE_TOOL_UNAVAILABLE = 'm365_unavailable';
export const PANE_TOOL_INVALID_INPUT = 'm365_invalid_input';
const CALL_ID = /^[A-Za-z0-9._:-]{1,100}$/;
const CAPABILITY = /^[A-Za-z0-9_-]{16,512}$/;
const TOOL_NAME = /^[a-z0-9_]{1,64}$/;
const ERROR_CODE = /^[a-z0-9_]{1,64}$/;
export function parsePaneToolCall(data) {
    const event = record(data);
    if (!event || typeof event.callId !== 'string' || !CALL_ID.test(event.callId)
        || typeof event.capability !== 'string' || !CAPABILITY.test(event.capability))
        return null;
    const tool = typeof event.tool === 'string' && TOOL_NAME.test(event.tool) ? event.tool : undefined;
    const input = record(event.input);
    const deadlineMs = event.deadlineMs === undefined || event.deadlineMs === null
        ? PANE_TOOL_DEFAULT_DEADLINE_MS
        : typeof event.deadlineMs === 'number' && Number.isFinite(event.deadlineMs) && event.deadlineMs > 0 ? event.deadlineMs : undefined;
    if (!tool || !input || deadlineMs === undefined)
        return { kind: 'invalid', callId: event.callId, capability: event.capability, tool: tool ?? 'unknown' };
    return { kind: 'call', call: { callId: event.callId, capability: event.capability, tool, input, deadlineMs } };
}
/** The host's share of the server's wait for one call: the clamped deadline less the posting reserve, never under half of it. */
export function paneToolWorkBudgetMs(deadlineMs) {
    const requested = typeof deadlineMs === 'number' && Number.isFinite(deadlineMs) ? deadlineMs : PANE_TOOL_DEFAULT_DEADLINE_MS;
    const deadline = Math.min(MAX_DEADLINE_MS, Math.max(MIN_DEADLINE_MS, requested));
    return Math.max(deadline - POST_RESERVE_MS, deadline / 2);
}
/** The host's answer bounded to what the server accepts. Never throws. */
export function boundPaneToolOutcome(outcome) {
    const answer = record(outcome);
    if (!answer || typeof answer.ok !== 'boolean')
        return failure(PANE_TOOL_UNAVAILABLE, 'The pane returned no answer.');
    if (!answer.ok) {
        const code = typeof answer.code === 'string' && ERROR_CODE.test(answer.code) ? answer.code : PANE_TOOL_UNAVAILABLE;
        return failure(code, typeof answer.message === 'string' ? answer.message : undefined);
    }
    // An ok result is a JSON object or array: null, a bare string or a number is a malformed answer (the server agrees).
    const result = answer.result;
    if (!result || typeof result !== 'object')
        return failure(PANE_TOOL_UNAVAILABLE, 'The pane produced no result.');
    let bytes;
    try {
        bytes = new TextEncoder().encode(JSON.stringify(result)).length;
    }
    catch {
        return failure(PANE_TOOL_UNAVAILABLE, 'The result could not be returned.');
    }
    if (bytes > PANE_TOOL_RESULT_MAX_BYTES)
        return failure(PANE_TOOL_UNAVAILABLE, 'The result was too large to return.');
    return { ok: true, result };
}
/** One streamed turn's pane tool calls. */
export class PaneToolTurn {
    options;
    seen = new Set();
    running = new Set();
    setTimer;
    clearTimer;
    warn;
    constructor(options) {
        this.options = options;
        this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
        this.warn = options.warn ?? ((message) => console.warn(message));
    }
    /** Calls accepted so far this turn. */
    get accepted() {
        return this.seen.size;
    }
    /**
     * Take one `pane_tool_call` event and return at once, so the stream reader is never held. False when nothing will be
     * posted: a frame with no usable call id or capability, a repeated call id, the 25th call, or a stopped turn.
     */
    dispatch(data) {
        if (this.options.signal?.aborted)
            return false;
        const parsed = parsePaneToolCall(data);
        if (!parsed) {
            this.warn('[gyro] ignored a pane tool call without a usable call id or capability');
            return false;
        }
        const callId = parsed.kind === 'call' ? parsed.call.callId : parsed.callId;
        if (this.seen.has(callId) || this.seen.size >= PANE_TOOL_MAX_CALLS_PER_TURN)
            return false;
        // A call whose answer could never be posted must not run (it may be a write): the server times it out instead.
        if (!isServiceApiPath(this.options.tools.resultPath)) {
            this.warn(`[gyro] pane tool ${parsed.kind === 'call' ? parsed.call.tool : parsed.tool} was not run: the host's resultPath is not a gyro-svc /v1 path`);
            return false;
        }
        this.seen.add(callId);
        const work = parsed.kind === 'call'
            ? this.run(parsed.call)
            : this.answer(parsed.tool, parsed.callId, parsed.capability, failure(PANE_TOOL_INVALID_INPUT, 'The tool call was malformed.'), Date.now());
        const tracked = work.catch(() => undefined).finally(() => this.running.delete(tracked));
        this.running.add(tracked);
        return true;
    }
    /** Settles when every accepted call has posted, or abandoned, its answer. */
    async whenIdle() {
        while (this.running.size)
            await Promise.all([...this.running]);
    }
    async run(call) {
        const started = Date.now();
        const budgetMs = paneToolWorkBudgetMs(call.deadlineMs);
        const work = new AbortController();
        const turn = this.options.signal;
        let cut;
        const cutoff = new Promise((resolve) => { cut = resolve; });
        let cutShort = false;
        const timer = this.setTimer(() => {
            cutShort = true;
            cut(failure(PANE_TOOL_UNAVAILABLE, 'The pane did not answer before the deadline.'));
        }, budgetMs);
        const stop = () => {
            cutShort = true;
            cut(failure(PANE_TOOL_UNAVAILABLE, 'The turn was stopped.'));
        };
        turn?.addEventListener('abort', stop, { once: true });
        let outcome;
        try {
            // Started inside a promise, so a host that throws synchronously is an error answer, not a lost call.
            const executing = Promise.resolve().then(() => this.options.tools.execute({
                callId: call.callId, tool: call.tool, input: call.input, deadlineMs: budgetMs, signal: work.signal,
            }));
            outcome = await Promise.race([
                executing.then(boundPaneToolOutcome, () => failure(PANE_TOOL_UNAVAILABLE, 'The pane could not run this tool.')),
                cutoff,
            ]);
        }
        finally {
            this.clearTimer(timer);
            turn?.removeEventListener('abort', stop);
        }
        // The deadline passed or the turn stopped: whatever the host still does is no longer wanted.
        if (cutShort)
            work.abort();
        // A stopped turn's server has already given up on this call: nothing to answer.
        if (turn?.aborted)
            return;
        await this.answer(call.tool, call.callId, call.capability, outcome, started);
    }
    async answer(tool, callId, capability, outcome, started) {
        const body = outcome.ok
            ? { callId, capability, ok: true, result: outcome.result }
            : { callId, capability, ok: false, error: outcome.message ? { code: outcome.code, message: outcome.message } : { code: outcome.code } };
        const code = outcome.ok ? 'ok' : outcome.code;
        try {
            const { status } = await this.options.post(this.options.tools.resultPath, body);
            if (status >= 200 && status < 300)
                return;
            // 404 and 409: gyro-svc already gave up on, or settled, the call. Anything else refused this answer.
            const gone = status === 404 || status === 409;
            this.warn(`[gyro] pane tool ${tool} (${code}, ${Date.now() - started} ms): ${gone ? 'gyro-svc had already closed the call' : 'gyro-svc refused the result'} (HTTP ${status})`);
        }
        catch (error) {
            this.warn(`[gyro] pane tool ${tool} (${code}, ${Date.now() - started} ms): posting the result failed (${error instanceof Error ? error.message : 'network error'})`);
        }
    }
}
function failure(code, message) {
    const text = message?.replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE_CHARS);
    return text ? { ok: false, code, message: text } : { ok: false, code };
}
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
//# sourceMappingURL=paneToolCalls.js.map